import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Banknote, CalendarDays, ChevronLeft, ChevronRight, Hourglass, Mail, Mailbox, Medal, PhoneCall, Receipt, Trophy } from 'lucide-react';
import { api } from '../lib/api';
import { MyAssignedEmails } from '../components/MyAssignedEmails';
import { formatUsd, localeUI } from '../lib/format';
import { useI18n } from '../lib/i18n';
import { useAuth, hasRole } from '../lib/auth';
import { useMyName } from '../lib/scope';
import { StatTile } from '../components/ui/StatTile';
import { DepreciationAlerts } from '../components/DepreciationAlerts';
import { EsperandoCheque } from '../components/EsperandoCheque';
import { FollowUpRow, type FollowUpItem } from './FollowUpQueue';
import { useClaimDrawer } from '../components/ClaimDrawer';

type WaitingCheck = {
  claimId: string;
  claimNumber: string | null;
  homeownerName: string | null;
  phase: 'awaiting_send' | 'in_transit';
  daysWaiting: number;
  color: 'orange' | 'green' | 'red';
};
type WaitClear = {
  checkId: string;
  claimId: string;
  claimNumber: string | null;
  homeownerName: string | null;
  contractorName: string | null;
  status: string | null;
  amountCents: number;
  days: number;
};
type DashboardData = {
  sentAckDays: number;
  arrivalDays: number;
  feeOwedCents: number;
  feeCollectedCents: number;
  feeDesde?: string | null;
  feeGapCents: number;
  waitingChecks: WaitingCheck[];
  waitingToClear: WaitClear[];
};

type CtItem = {
  claimId: string;
  name: string | null;
  days: number;
  sentOn?: string | null;
  sentBy?: string | null;
  assignedPerson?: string | null;
  /** El claim team dueño. El FILTRO va por acá; debajo de la barra va assignedPerson. */
  paymentsTeam?: string | null;
  contractor?: string | null;
  /** depreciation: el COC no se ha enviado — el reloj de la etapa no corre. */
  noCoc?: boolean;
  /** La espera esta parada porque falta un documento (final invoice del contratista, etc.). */
  waitingDocs?: boolean;
  waitingDocsNote?: string | null;
  /** Dias TRABADO, que no es lo mismo que los dias en la etapa. */
  waitingDocsDays?: number;
  /** enviados: en cuántos días se CONSIGUIÓ la fecha (récord de la fase 1). */
  gotDateInDays?: number;
};
type WithCheck = { claimId: string; name: string | null; who: string; contractorName: string | null; assignedPerson?: string | null; paymentsTeam?: string | null; days: number };
type OwedItem = { claimId: string; name: string | null; days: number; contractorName?: string | null; assignedPerson?: string | null; paymentsTeam?: string | null };
type ArrivedCheck = {
  id: string;
  amount_cents: string | number;
  homeowner_name: string | null;
  claim_number: string | null;
  contractor_name: string | null;
};
type CheckTracking = {
  stages: Record<string, { waiting: CtItem[]; sent: CtItem[] }>;
  withCheck: WithCheck[];
  owedToUs: OwedItem[];
};
type EmployeeMonth = { month: string; employees: { name: string; count: number }[] };
type InvoiceActivity = {
  date: string;
  created: Array<{ person: string; count: number }>;
  reviewed: Array<{ person: string; count: number }>;
  issued: number;
};

const CT_STAGES: { stage: string; key: string }[] = [
  { stage: 'acv', key: 'cd.stageAcv' },
  { stage: 'depreciation', key: 'cd.stageDepreciation' },
  { stage: 'repair', key: 'cd.stageRepair' },
  { stage: 'supplement', key: 'cd.stageSupplement' },
  { stage: 'bounced', key: 'cd.stageBounced' },
  { stage: 'mortgage', key: 'cd.stageMortgage' },
];

/** Qué COLA de followups corresponde a cada etapa del reloj — para el modal embebido
 *  (pedido de Manuel 2026-08-10, versión 2: "no quiero que vaya a otra sección, quiero que
 *  abra ahí mismo una ventana en el medio con la info de esa sección").
 *  `bounced` no tiene cola propia — esas barras no abren nada. */
const STAGE_FUQ: Record<string, string> = {
  acv: 'acv', depreciation: 'depreciation', repair: 'approved-repair',
  supplement: 'supplement', mortgage: 'mortgage',
};

/** La MISMA fila de la cola (CheckSentBox, adjusters, updates…) en una ventana centrada. */
function QueuePeek({ stage, claimId, onClose }: { stage: string; claimId: string; onClose: () => void }) {
  const { t } = useI18n();
  const cd = useClaimDrawer();
  const [item, setItem] = useState<FollowUpItem | null>(null);
  const [gone, setGone] = useState(false);
  const load = () => {
    api.get<{ claims: FollowUpItem[] }>(`/api/followups/queue/${stage}`)
      .then((r) => {
        const it = r.claims.find((x) => String(x.id) === String(claimId));
        if (it) { setItem(it); setGone(false); } else setGone(true);
      })
      .catch(() => setGone(true));
  };
  useEffect(load, [stage, claimId]); // eslint-disable-line react-hooks/exhaustive-deps
  return (
    <div onClick={onClose} style={{ position: 'fixed', inset: 0, zIndex: 500, background: 'rgba(0,0,0,0.5)', display: 'grid', placeItems: 'center', padding: 14 }}>
      <div className="card" onClick={(e) => e.stopPropagation()}
        style={{ width: 'min(1040px, 96vw)', maxHeight: '90vh', overflowY: 'auto', margin: 0, padding: 12, position: 'relative' }}>
        <button className="btn ghost sm" onClick={onClose} aria-label={t('common.close')}
          style={{ position: 'absolute', top: 8, right: 8, zIndex: 1 }}>✕</button>
        {gone
          ? <div className="muted" style={{ padding: 20 }}>{t('dash.peekGone')}</div>
          : !item
          ? <div className="muted" style={{ padding: 20 }}>{t('common.loadingDots')}</div>
          : <FollowUpRow item={item} stage={stage} layout="card" open onToggle={() => {}} onLogged={load} onClient={cd.open} />}
        {cd.drawer}
      </div>
    </div>
  );
}

export function Dashboard() {
  const { t } = useI18n();
  const { me } = useAuth();
  // Modal embebido con la fila de la cola (click en una barra de los relojes).
  const [colaModal, setColaModal] = useState<{ stage: string; claimId: string } | null>(null);
  const navigate = useNavigate();
  // Los totales de plata solo los ve quien PUEDE HACER PAGOS (can_make_payments) o el master.
  // El backend además los quita del payload — esto solo decide si se pinta el bloque.
  const canSeeMoney = me?.kind === 'tenant' && (me.user.can_make_payments === true || me.user.role === 'master');
  const [d, setD] = useState<DashboardData | null>(null);
  const [ct, setCt] = useState<CheckTracking | null>(null);
  const [arrived, setArrived] = useState<ArrivedCheck[]>([]);
  // Extras del Panel MÓVIL (serie mensual + inspecciones de hoy) — solo se piden en ≤900px.
  type MobileExtras = { monthly: Array<{ mes: string; cents: number }> | null; todayInspections: Array<{ id: string; claimId: string | null; homeowner: string | null; type: string; at: string | null; inspector: string | null }> };
  const [mext, setMext] = useState<MobileExtras | null>(null);
  useEffect(() => {
    if (!window.matchMedia('(max-width: 900px)').matches) return;
    api.get<MobileExtras>('/api/dashboard/mobile').then(setMext).catch(() => {});
  }, []);
  const [owner, setOwner] = useState(''); // filtro por persona asignada ('' = todos)
  const [ownerDefaulted, setOwnerDefaulted] = useState(false);
  const myName = useMyName();
  const [busy, setBusy] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);

  function loadDashboard() {
    api.get<DashboardData>('/api/dashboard').then(setD).catch(() => setErr('x'));
  }
  // Actividad de invoices del día elegido (quién hizo / quién revisó, con cuántos).
  const [invDay, setInvDay] = useState<string>(() => new Date().toISOString().slice(0, 10));
  const [invAct, setInvAct] = useState<InvoiceActivity | null>(null);
  useEffect(() => {
    setInvAct(null);
    api.get<InvoiceActivity>(`/api/reports/invoice-activity?date=${invDay}`).then(setInvAct).catch(() => {});
  }, [invDay]);

  function loadArrived() {
    const today = new Date().toISOString().slice(0, 10);
    api.get<{ checks: ArrivedCheck[] }>(`/api/checks/received?date=${today}`).then((r) => setArrived(r.checks)).catch(() => {});
  }
  useEffect(() => {
    loadDashboard();
    api.get<CheckTracking>('/api/reports/check-tracking').then(setCt).catch(() => {});
    loadArrived();
  }, []);

  // Ranking de fechado POR MES (pedido 2026-08-09: el histórico no dice quién trabaja AHORA).
  const [empMonth, setEmpMonth] = useState<string>(() => new Date().toISOString().slice(0, 7));
  const [emp, setEmp] = useState<EmployeeMonth | null>(null);
  useEffect(() => {
    setEmp(null);
    api.get<EmployeeMonth>(`/api/reports/check-dates-employees?month=${empMonth}`).then(setEmp).catch(() => {});
  }, [empMonth]);
  const shiftEmpMonth = (delta: number) => {
    const [y, m] = empMonth.split('-').map(Number);
    const d0 = new Date(y!, (m! - 1) + delta, 15);
    setEmpMonth(`${d0.getFullYear()}-${String(d0.getMonth() + 1).padStart(2, '0')}`);
  };

  async function markAvailable(checkId: string) {
    setBusy(checkId);
    try {
      await api.post(`/api/checks/${checkId}/clear`);
      loadDashboard();
    } catch { /* ignore */ } finally { setBusy(null); }
  }

  // EL FILTRO VA POR EQUIPO, no por persona (pedido 2026-08-27): "joe, ornella, mario y todos los
  // de payments pertenecen a un team; si algún claim está asignado a ellos individual debés
  // colocar el nombre del team instead". Antes la lista mezclaba los cuatro claim teams con las
  // cuatro personas —porque en la base hay equipos viejos de UNA persona en el depto payments,
  // llamados con su nombre— y filtrar por "Ornella Matos" dejaba fuera los claims de "Scarlett's
  // Team", que son los MISMOS de ella. El backend ya resuelve persona → su claim team.
  const owners = useMemo(() => {
    if (!ct) return [];
    const s = new Set<string>();
    const add = (it: { paymentsTeam?: string | null; assignedPerson?: string | null }) => {
      const v = it.paymentsTeam || it.assignedPerson;
      if (v) s.add(v);
    };
    for (const k of Object.keys(ct.stages)) {
      for (const it of [...ct.stages[k]!.waiting, ...ct.stages[k]!.sent]) add(it);
    }
    for (const w of ct.withCheck) add(w);
    return [...s].sort();
  }, [ct]);

  // Default: office ve sus claims; manager+ ve todos. Igual puede cambiar el filtro.
  useEffect(() => {
    if (ownerDefaulted || !myName || owners.length === 0) return;
    if (!hasRole(me, 'manager')) {
      // Mi equipo: el del primer claim donde YO soy la persona de pagos. Buscar mi nombre dentro
      // de los nombres del filtro dejó de servir cuando el filtro pasó a ser por equipo
      // ("Ornella Matos" no está adentro de "Scarlett's Team").
      const míoPorPersona = (() => {
        const iguales = (a?: string | null) => !!a && a.toLowerCase().includes(myName.toLowerCase());
        for (const k of Object.keys(ct?.stages ?? {})) {
          for (const it of [...(ct?.stages[k]?.waiting ?? []), ...(ct?.stages[k]?.sent ?? [])]) {
            if (iguales(it.assignedPerson)) return it.paymentsTeam || it.assignedPerson || null;
          }
        }
        for (const w of ct?.withCheck ?? []) if (iguales(w.assignedPerson)) return w.paymentsTeam || w.assignedPerson || null;
        return null;
      })();
      const mine = míoPorPersona ?? owners.find((o) => o.toLowerCase().includes(myName.toLowerCase()));
      if (mine) setOwner(mine);
    }
    setOwnerDefaulted(true);
  }, [myName, owners, ownerDefaulted, me, ct]);

  // Se filtra por el EQUIPO (con fallback al nombre suelto de quien no tiene claim team).
  const byOwner = <T extends { assignedPerson?: string | null; paymentsTeam?: string | null }>(items: T[]) =>
    owner ? items.filter((i) => (i.paymentsTeam || i.assignedPerson) === owner) : items;

  if (err) return <div className="card">{t('common.error')}</div>;
  if (!d) return <div className="page-empty">{t('common.loading')}</div>;

  const wtcTotal = d.waitingToClear.reduce((s, w) => s + w.amountCents, 0);

  return (
    <div className="grid" style={{ gap: 18 }}>
      {/* PANEL MÓVIL (mockup B-mobile "4 · Dashboard", solo ≤900px por CSS): saludo + fecha con
          los cheques de HOY + KPIs 2×2 con datos reales. La serie de 12 meses y el feed del
          mockup no tienen endpoint todavía — no se inventan números. */}
      <div className="mdash">
        <div className="mdash-greet">
          {(() => { const h = new Date().getHours(); return t(h < 12 ? 'mdash.morning' : h < 19 ? 'mdash.afternoon' : 'mdash.evening'); })()}
          {me?.kind === 'tenant' ? `, ${(me.user.name || '').split(' ')[0]}` : ''}
        </div>
        <div className="mdash-date">
          {new Date().toLocaleDateString(undefined, { weekday: 'long', month: 'short', day: 'numeric' })}
          {' · '}
          {arrived.length === 0 ? t('mdash.checks0Today') : arrived.length === 1 ? t('mdash.check1Today') : t('mdash.checksToday').replace('{n}', String(arrived.length))}
        </div>
        <div className="mdash-kpis">
          {canSeeMoney && (
            <>
              <div className="card mdash-kpi"><div className="k">{t('dash.feeOwed')}</div><div className="v">{formatUsd(d.feeOwedCents)}</div></div>
              <div className="card mdash-kpi"><div className="k">{t('dash.feeCollected')}</div><div className="v">{formatUsd(d.feeCollectedCents)}</div></div>
              <div className="card mdash-kpi"><div className="k">{t('dash.gap')}</div><div className="v">{formatUsd(d.feeGapCents)}</div></div>
            </>
          )}
          <div className="card mdash-kpi"><div className="k">{t('mdash.wtc')}</div><div className="v">{d.waitingToClear.length}</div></div>
        </div>
        {/* Gráfica del mockup: fee COBRADO por mes (misma fuente que el total — billing/feeTotals).
            Solo sale si hay serie (los datos arrancan el 2026-06-22; no se inventan meses). */}
        {canSeeMoney && mext?.monthly && mext.monthly.length > 1 && (
          <div className="card mdash-chart">
            <div className="t">{t('mdash.chartTitle')}</div>
            <div className="s">{t('mdash.chartSub').replace('{n}', String(mext.monthly.length))}</div>
            <MonthlySpark data={mext.monthly.map((m) => m.cents)} />
          </div>
        )}
        {/* Feed del día (mockup): cheques que llegaron hoy + inspecciones de hoy. */}
        {(arrived.length > 0 || (mext?.todayInspections.length ?? 0) > 0) && (
          <div className="card mdash-feed">
            <div className="t">{t('mdash.today')}</div>
            {arrived.slice(0, 5).map((ck) => (
              <div key={`ck-${ck.id}`} className="mdash-fi">
                <span className="fi good"><Banknote size={14} strokeWidth={1.75} /></span>
                <div>
                  <b>{t('mdash.checkArrived')} — <span style={{ fontFamily: 'var(--font-mono)' }}>{formatUsd(Number(ck.amount_cents))}</span></b>
                  <span>{ck.homeowner_name || ck.claim_number || '—'}</span>
                </div>
              </div>
            ))}
            {(mext?.todayInspections ?? []).slice(0, 6).map((ins) => (
              <div key={`in-${ins.id}`} className="mdash-fi">
                <span className="fi info"><CalendarDays size={14} strokeWidth={1.75} /></span>
                <div>
                  <b>{t('mdash.inspection')} {ins.at ? new Date(ins.at).toLocaleTimeString(localeUI(), { hour: 'numeric', minute: '2-digit' }) : ''}</b>
                  <span>{ins.homeowner || '—'}{ins.inspector ? ` · ${ins.inspector}` : ''}</span>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
      {/* ══ LAS GRÁFICAS DE CHEQUES VAN DE PRIMERAS (pedido del dueño 2026-08-09): son el
          trabajo diario de Payments — sus cheques, sus deadlines, los que vienen (enviados)
          y los que faltan por fechar (esperando). El filtro y la leyenda van pegados. ══ */}
      {owners.length > 0 && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <span className="muted" style={{ fontSize: 'var(--fs-sm)' }}>{t('dash.filterOwner')}:</span>
          <select className="input" style={{ maxWidth: 240 }} value={owner} onChange={(e) => setOwner(e.target.value)}>
            <option value="">{t('dash.allOwners')}</option>
            {owners.map((o) => <option key={o} value={o}>{o}</option>)}
          </select>
        </div>
      )}
      {ct && (
        <div className="dash-legend">
          <span className="dash-legend-item"><span className="dot orange" /> {t('dash.legendAck', { a: String(d.sentAckDays) })}</span>
          <span className="dash-legend-item"><span className="dot green" /> {t('dash.legendTransit', { b: String(d.arrivalDays) })}</span>
          <span className="dash-legend-item"><span className="dot red" /> {t('dash.legendOverdue')}</span>
        </div>
      )}

      {/* Cheques por etapa: esperando (sin fecha) vs enviado (días desde el envío). El reloj de
          depreciation arranca con el COC ENVIADO (sin COC la fila va aparte) y el de supplement
          con el Resolved del board — decisión del dueño 2026-08-09. */}
      {ct && CT_STAGES.map((s) => {
        const st = ct.stages[s.stage];
        if (!st) return null;
        const all = byOwner(st.waiting);
        const waiting = all.filter((i) => !i.noCoc); // sin COC enviado no hay reloj (van en la cola To Send del COC)
        const sent = byOwner(st.sent);
        if (all.length === 0 && sent.length === 0) return null;
        return (
          <div className="card" key={s.stage}>
            <h2 className="card-title" style={{ display: 'flex', alignItems: 'baseline', gap: 10, flexWrap: 'wrap' }}>
              {t(s.key)}
              {s.stage === 'depreciation' && <span className="muted" style={{ fontSize: 'var(--fs-2xs)', fontWeight: 'var(--fw-body)' }}>{t('dash.depClockNote')}</span>}
              {s.stage === 'supplement' && <span className="muted" style={{ fontSize: 'var(--fs-2xs)', fontWeight: 'var(--fw-body)' }}>{t('dash.supClockNote')}</span>}
            </h2>
            {/* Esperando arriba, enviado abajo (mismo bloque). */}
            <div className="muted" style={{ fontSize: 'var(--fs-2xs)', marginBottom: 6 }}><Hourglass size={12} strokeWidth={1.75} style={{ verticalAlign: 'text-bottom' }} /> {t('dash.ctWaiting')} <span className="badge gray">{waiting.length}</span></div>
            <VBars items={waiting.map((i) => ({
              id: i.claimId, name: i.name ?? '—', value: i.days, contractor: i.contractor, owner: i.assignedPerson,
              // 📄 = la espera está parada porque falta un papel (pedido del dueño 2026-08-27).
              // Azul y no rojo a propósito: el rojo dice "se les pasó", y esto no es eso.
              flag: i.waitingDocs
                ? { color: 'var(--info)', icon: '📄',
                    title: `${t('dash.waitingDocs')}${i.waitingDocsNote ? `: ${i.waitingDocsNote}` : ''}${i.waitingDocsDays != null ? ` (${i.waitingDocsDays}d)` : ''}` }
                : null,
            }))} color={(v) => (v > d.sentAckDays ? 'var(--red)' : 'var(--orange)')}
              onBar={STAGE_FUQ[s.stage] ? (claimId) => setColaModal({ stage: STAGE_FUQ[s.stage]!, claimId }) : undefined} />
            {/* La sección "COC not sent" se QUITÓ del dashboard (pedido de Manuel 2026-08-10):
                esos claims ya viven en la cola To Send del módulo COC — acá solo hacían ruido.
                Siguen EXCLUIDOS del reloj de arriba (waiting filtra noCoc): sin COC enviado el
                reloj de la etapa no corre. */}
            <div className="muted" style={{ fontSize: 'var(--fs-2xs)', margin: '14px 0 6px', paddingTop: 12, borderTop: '1px solid var(--line)' }}><Mailbox size={12} strokeWidth={1.75} style={{ verticalAlign: 'text-bottom' }} /> {t('dash.ctSent')} <span className="badge gray">{sent.length}</span></div>
            {/* El sub del enviado es el RÉCORD de la fase 1: "fecha en 3d" — el conteo viejo no
                se pierde al fecharse, queda como marca de cuánto costó conseguir la fecha. */}
            <VBars items={sent.map((i) => ({ id: i.claimId + (i.sentOn ?? ''), claimId: i.claimId, name: i.name ?? '—', value: i.days, sub: i.gotDateInDays != null ? t('dash.gotDate', { n: String(i.gotDateInDays) }) : (i.sentBy ?? undefined), contractor: i.contractor, owner: i.assignedPerson }))} color={(v) => (v > d.arrivalDays ? 'var(--red)' : 'var(--green)')}
              onBar={STAGE_FUQ[s.stage] ? (claimId) => setColaModal({ stage: STAGE_FUQ[s.stage]!, claimId }) : undefined} />
          </div>
        );
      })}

      {/* Alertas de cheques (reemplaza el Follow-up overdue genérico, pedido 2026-08-09): lo
          vencido de las MISMAS gráficas de arriba, como lista accionable — sin fecha tras el
          umbral ("llamar al seguro a fechar") y enviado que no llega ("llamar al seguro"). */}
      {ct && <CheckAlerts ct={ct} byOwner={byOwner} sentAckDays={d.sentAckDays} arrivalDays={d.arrivalDays} />}

      <MyAssignedEmails />
      <DepreciationAlerts />
      {/* Lo que el seguro aprobó y todavía no pagó. Va junto a las alertas de depreciación
          porque son la misma clase de cosa: plata con fecha de vencimiento que se pierde
          si nadie la persigue. Se oculta sola cuando no hay nada esperando. */}
      <EsperandoCheque />
      {/* Totales de plata: solo super-manager+. Ancho fijo para que no se estire. */}
      {canSeeMoney && (
        <div className="dash-money-desktop" style={{ maxWidth: 760 }}>
          <div className="grid cols-3">
            <StatTile label={t('dash.feeOwed')} value={<span style={{ fontFamily: 'var(--font-mono)' }}>{formatUsd(d.feeOwedCents)}</span>} sub={t('dash.feeOwedSub')} />
            <StatTile label={t('dash.feeCollected')} value={<span style={{ fontFamily: 'var(--font-mono)' }}>{formatUsd(d.feeCollectedCents)}</span>} sub={t('dash.feeCollectedSub')} />
            <StatTile label={t('dash.gap')} value={<span style={{ fontFamily: 'var(--font-mono)' }}>{formatUsd(d.feeGapCents)}</span>} sub={t('dash.gapSub')} />
          </div>
          {/* DESDE CUÁNDO. Los eventos arrancan el 22-jun-2026: la cartera importada de Monday
              quedó afuera. Leer estos números como históricos es justo lo que hacía creíble un
              gap de −$2,87 millones, así que el corte se dice acá y no se supone. */}
          {d.feeDesde && (
            <div className="muted" style={{ fontSize: 'var(--fs-2xs)', marginTop: 4 }}>
              {t('dash.feeSince').replace('{d}', new Date(d.feeDesde).toLocaleDateString(localeUI()))}
            </div>
          )}
        </div>
      )}

      {/* Esperando clear + llegaron hoy, lado a lado, ancho fijo. */}
      <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap' }}>
        <div className="card" style={{ flex: '1 1 560px', maxWidth: 680 }}>
          <div style={{ display: 'flex', alignItems: 'baseline', gap: 10, flexWrap: 'wrap', marginBottom: 8 }}>
            <h2 className="card-title" style={{ margin: 0 }}>{t('dash.waitingClear')}</h2>
            <span className="badge gray">{d.waitingToClear.length}</span>
            <span style={{ flex: 1 }} />
            <span className="muted" style={{ fontSize: 'var(--fs-sm)' }}>{t('dash.total')}: <strong style={{ color: 'var(--ink)', fontFamily: 'var(--font-mono)' }}>{formatUsd(wtcTotal)}</strong></span>
          </div>
          {d.waitingToClear.length === 0 ? (
            <div className="muted">{t('common.none')}</div>
          ) : (
            <div style={{ maxHeight: 280, overflowY: 'auto' }}>
              <table className="tbl">
                <thead>
                  <tr>
                    <th>{t('claims.homeowner')}</th>
                    <th>{t('cd.contractorOpt')}</th>
                    <th>{t('common.status')}</th>
                    <th className="num">{t('common.amount')}</th>
                    <th className="num">{t('q.days')}</th>
                    {canSeeMoney && <th></th>}
                  </tr>
                </thead>
                <tbody>
                  {d.waitingToClear.map((w) => (
                    <tr key={w.checkId}>
                      <td>
                        <Link to={`/claims/${w.claimId}`}>{w.homeownerName || w.claimNumber || '—'}</Link>
                      </td>
                      <td className="muted">{w.contractorName || '—'}</td>
                      <td>{w.status ? <span className="badge gray">{w.status}</span> : '—'}</td>
                      <td className="num" style={{ fontFamily: 'var(--font-mono)' }}>{formatUsd(w.amountCents)}</td>
                      <td className="num">{w.days}d</td>
                      {canSeeMoney && (
                        <td className="num">
                          <button className="btn sm" disabled={busy === w.checkId} onClick={() => markAvailable(w.checkId)}>
                            {busy === w.checkId ? '…' : t('dash.markAvailable')}
                          </button>
                        </td>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Cheques que llegaron HOY: homeowner, contratista, monto. */}
        <div className="card" style={{ flex: '1 1 420px', maxWidth: 560 }}>
          <div style={{ display: 'flex', alignItems: 'baseline', gap: 10, marginBottom: 8 }}>
            <h2 className="card-title" style={{ margin: 0 }}>{t('dash.arrivedToday')}</h2>
            <span className="badge gray">{arrived.length}</span>
          </div>
          {arrived.length === 0 ? (
            <div className="muted">{t('common.none')}</div>
          ) : (
            <div style={{ maxHeight: 280, overflowY: 'auto' }}>
              <table className="tbl">
                <thead>
                  <tr>
                    <th>{t('claims.homeowner')}</th>
                    <th>{t('cd.contractorOpt')}</th>
                    <th className="num">{t('common.amount')}</th>
                  </tr>
                </thead>
                <tbody>
                  {arrived.map((a) => (
                    <tr key={a.id}>
                      <td>{a.homeowner_name || a.claim_number || '—'}</td>
                      <td className="muted">{a.contractor_name || '—'}</td>
                      <td className="num" style={{ fontFamily: 'var(--font-mono)' }}>{formatUsd(a.amount_cents)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* INVOICES POR DÍA — quién los hizo y quién los revisó (pedido del dueño). El día se
          navega con ◀ ▶; sale del audit log, así que borrar el invoice después no borra el
          trabajo hecho ese día. */}
      <div className="card">
        <h2 className="card-title" style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
          <Receipt size={14} strokeWidth={1.75} style={{ verticalAlign: 'text-bottom' }} /> {t('dash.invActivity')}
          <span style={{ flex: 1 }} />
          <button className="btn ghost sm" aria-label={t('common.prevDay')} title={t('common.prevDay')} onClick={() => { const dd = new Date(invDay + 'T12:00:00'); dd.setDate(dd.getDate() - 1); setInvDay(dd.toISOString().slice(0, 10)); }}><ChevronLeft size={14} strokeWidth={1.75} /></button>
          <input className="input sm" type="date" style={{ width: 150 }} value={invDay} onChange={(e) => e.target.value && setInvDay(e.target.value)} />
          <button className="btn ghost sm" aria-label={t('common.nextDay')} title={t('common.nextDay')} onClick={() => { const dd = new Date(invDay + 'T12:00:00'); dd.setDate(dd.getDate() + 1); setInvDay(dd.toISOString().slice(0, 10)); }}><ChevronRight size={14} strokeWidth={1.75} /></button>
        </h2>
        {!invAct ? <div className="muted" style={{ fontSize: 'var(--fs-sm)' }}>…</div> : (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(260px, 100%), 1fr))', gap: 14 }}>
            {([['created', t('dash.invMade')], ['reviewed', t('dash.invReviewed')]] as const).map(([k, label]) => {
              const rowsK = invAct[k];
              const total = rowsK.reduce((n, r) => n + r.count, 0);
              const max = Math.max(...rowsK.map((r) => r.count), 1);
              return (
                <div key={k}>
                  <div className="muted" style={{ fontSize: 'var(--fs-2xs)', marginBottom: 6 }}>
                    {label} <span className="badge gray">{total}</span>
                  </div>
                  {rowsK.length === 0 ? <div className="muted" style={{ fontSize: 'var(--fs-2xs)' }}>—</div> : (
                    <div style={{ display: 'grid', gap: 6 }}>
                      {rowsK.map((r, i) => (
                        <div key={r.person} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                          <span style={{ width: 18, textAlign: 'right', fontWeight: 'var(--fw-bold)', fontSize: 'var(--fs-2xs)', display: 'inline-flex', justifyContent: 'flex-end' }}>{i === 0 ? <Trophy size={13} strokeWidth={1.75} color="#d4a017" /> : i === 1 ? <Medal size={13} strokeWidth={1.75} color="#9ca3af" /> : i === 2 ? <Medal size={13} strokeWidth={1.75} color="#b45309" /> : i + 1}</span>
                          <span style={{ width: 140, fontSize: 'var(--fs-sm)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }} title={r.person}>{r.person}</span>
                          <div style={{ flex: 1, background: 'var(--surface-2)', borderRadius: 6, height: 16 }}>
                            <div style={{ width: `${(r.count / max) * 100}%`, background: k === 'created' ? 'var(--chart-1)' : 'var(--purple)', height: '100%', borderRadius: 6 }} />
                          </div>
                          <span style={{ width: 30, textAlign: 'right', fontWeight: 'var(--fw-bold)', fontSize: 'var(--fs-2xs)' }}>{r.count}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
        {invAct && invAct.issued > 0 && (
          <div className="muted" style={{ fontSize: 'var(--fs-2xs)', marginTop: 8 }}><Mail size={12} strokeWidth={1.75} style={{ verticalAlign: 'text-bottom' }} /> {t('dash.invIssued', { n: String(invAct.issued) })}</div>
        )}
      </div>

      {ct && byOwner(ct.owedToUs).length > 0 && (
        <div className="card">
          <h2 className="card-title">{t('dash.ctOwed')}</h2>
          <VBars
            /* El nombre se recorta en la barra ("Alberta …") y sin poder abrirlo no hay forma de
               saber de quién se trata ni si ya se le mandó el invoice (reporte de Gabriela,
               2026-09-10). El id ya viaja: solo faltaba el clic. */
            items={byOwner(ct.owedToUs).map((o, i) => ({ id: (o.claimId ?? '') + i, claimId: o.claimId, name: o.name ?? '—', value: o.days, contractor: o.contractorName, owner: o.assignedPerson }))}
            color={(v) => (v > 7 ? 'var(--red)' : 'var(--info)')}
            onBar={(claimId) => navigate(`/claims/${claimId}`)}
          />
        </div>
      )}

      {ct && (
        <div className={hasRole(me, 'manager') ? 'grid cols-2' : 'grid'}>
          <div className="card">
            <h2 className="card-title">{t('dash.ctWithCheck')}</h2>
            <VBars
              items={byOwner(ct.withCheck).map((w, i) => ({ id: (w.claimId ?? '') + i, claimId: w.claimId, name: `${w.who === 'contractor' ? 'CO' : 'HO'} · ${w.name ?? '—'}`, value: w.days, contractor: w.contractorName, owner: w.assignedPerson }))}
              /* El color sube con los días, que es el dato que pidió el dueño ("cuenta los días de
                 cuánto tiempo lo tiene ahí"). Plano en naranja, un cliente con el cheque hace 65
                 días se veía igual que uno de 15 — y son dos llamadas muy distintas. */
              color={(v) => (v > 30 ? 'var(--red)' : v > 14 ? 'var(--orange)' : 'var(--info)')}
              onBar={(claimId) => navigate(`/claims/${claimId}`)}
            />
          </div>
          {/* Ranking de empleados: su endpoint es manager+ — a office se le esconde la card
              (antes le quedaba un "…" eterno). El resto del dashboard ya llega filtrado a
              LO SUYO desde el servidor (2026-08-11). */}
          {hasRole(me, 'manager') && <div className="card">
            <h2 className="card-title" style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
              {t('dash.ctEmployees')}
              <span style={{ flex: 1 }} />
              {/* POR MES (pedido 2026-08-09): el total histórico no dice quién trabaja AHORA. */}
              <button className="btn ghost sm" aria-label={t('common.prevMonth')} title={t('common.prevMonth')} onClick={() => shiftEmpMonth(-1)}><ChevronLeft size={14} strokeWidth={1.75} /></button>
              <span style={{ fontSize: 'var(--fs-sm)', fontWeight: 'var(--fw-bold)', minWidth: 84, textAlign: 'center' }}>
                {new Date(`${empMonth}-15T12:00:00`).toLocaleDateString(localeUI(), { month: 'short', year: 'numeric' })}
              </span>
              <button className="btn ghost sm" aria-label={t('common.nextMonth')} title={t('common.nextMonth')} disabled={empMonth >= new Date().toISOString().slice(0, 7)} onClick={() => shiftEmpMonth(1)}><ChevronRight size={14} strokeWidth={1.75} /></button>
            </h2>
            <div className="muted" style={{ fontSize: 'var(--fs-2xs)', marginBottom: 6 }}>{t('dash.ctEmployeesHelp')}</div>
            {/* Serie única sin semáforo: el color de gráficas del design system. */}
            {!emp ? <div className="muted" style={{ fontSize: 'var(--fs-sm)' }}>…</div> : (
              <VBars items={emp.employees.map((e, i) => ({ id: e.name + i, name: e.name, value: e.count }))} color={() => 'var(--chart-1)'} unit="" />
            )}
          </div>}
        </div>
      )}

      {colaModal && <QueuePeek stage={colaModal.stage} claimId={colaModal.claimId} onClose={() => setColaModal(null)} />}
    </div>
  );
}

/**
 * ALERTAS DE CHEQUES — lo VENCIDO de las gráficas de arriba como lista accionable (reemplaza el
 * Follow-up overdue genérico). Sale de los MISMOS datos que las gráficas (ct + umbrales), así
 * los números nunca divergen entre la barra roja y la alerta.
 *  · esperando > sentAckDays  → "Nd sin fecha de envío — llamar al seguro"
 *  · enviado   > arrivalDays  → "enviado hace Nd y no llega — llamar al seguro"
 */
function CheckAlerts({ ct, byOwner, sentAckDays, arrivalDays }: {
  ct: CheckTracking;
  byOwner: <T extends { assignedPerson?: string | null }>(items: T[]) => T[];
  sentAckDays: number;
  arrivalDays: number;
}) {
  const { t } = useI18n();
  type Alerta = { claimId: string; name: string | null; stageKey: string; days: number; kind: 'no_date' | 'not_arrived'; owner: string | null };
  const alertas: Alerta[] = [];
  for (const s of CT_STAGES) {
    const st = ct.stages[s.stage];
    if (!st) continue;
    for (const i of byOwner(st.waiting)) {
      // `waitingDocs` fuera: la alerta dice "llamar al seguro a fechar" y ESA es justo la llamada
      // que no se puede hacer hasta que llegue el papel. Sin este filtro la misma pantalla pintaba
      // la barra azul con 📄 arriba y, cuatro tarjetas más abajo, el mismo claim en la lista roja
      // pidiendo la acción contraria. Sigue contado en la barra: se saca del reclamo, no de la vista.
      if (!i.noCoc && !i.waitingDocs && i.days > sentAckDays) alertas.push({ claimId: i.claimId, name: i.name, stageKey: s.key, days: i.days, kind: 'no_date', owner: i.assignedPerson ?? null });
    }
    for (const i of byOwner(st.sent)) {
      if (i.days > arrivalDays) alertas.push({ claimId: i.claimId, name: i.name, stageKey: s.key, days: i.days, kind: 'not_arrived', owner: i.assignedPerson ?? null });
    }
  }
  alertas.sort((a, b) => b.days - a.days);
  // Filtro propio por PAYMENT OWNER (pedido 2026-08-11): con 444 alertas la lista solo sirve
  // si cada quien puede ver LO SUYO sin tocar el filtro global de arriba.
  const [own, setOwn] = useState('');
  const dueños = [...new Set(alertas.map((a) => a.owner).filter(Boolean) as string[])].sort();
  const visibles = own ? alertas.filter((a) => (a.owner ?? '') === own) : alertas;
  if (alertas.length === 0) return null;
  return (
    <div className="card" style={{ borderLeft: '4px solid var(--red)' }}>
      <h2 className="card-title" style={{ margin: 0, display: 'flex', alignItems: 'center', gap: 8, color: 'var(--red)', flexWrap: 'wrap' }}>
        <PhoneCall size={16} strokeWidth={1.75} /> {t('dash.checkAlerts')} <span className="badge" style={{ background: 'var(--red-soft)', color: 'var(--red)' }}>{visibles.length}</span>
        <span style={{ flex: 1 }} />
        <select className="input sm" style={{ maxWidth: 200, fontWeight: 'var(--fw-body)', color: 'var(--ink)' }} value={own} onChange={(e) => setOwn(e.target.value)}>
          <option value="">{t('dash.allOwners')}</option>
          {dueños.map((o) => <option key={o} value={o}>{o}</option>)}
        </select>
      </h2>
      <div style={{ display: 'grid', gap: 6, marginTop: 10, maxHeight: 300, overflowY: 'auto' }}>
        {visibles.map((a) => (
          <div key={`${a.claimId}-${a.stageKey}-${a.kind}`} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '6px 0', borderBottom: '1px solid var(--line)', flexWrap: 'wrap' }}>
            <Link to={`/claims/${a.claimId}`} style={{ fontWeight: 600 }}>{a.name || `#${a.claimId}`}</Link>
            <span className="badge gray" style={{ fontSize: 'var(--fs-2xs)' }}>{t(a.stageKey)}</span>
            {a.owner && <span className="muted" style={{ fontSize: 'var(--fs-2xs)' }}>{a.owner}</span>}
            <span style={{ flex: 1 }} />
            <span style={{ fontSize: 'var(--fs-2xs)', color: 'var(--red)', fontWeight: 'var(--fw-bold)' }}>
              {t(a.kind === 'no_date' ? 'dash.alertNoDate' : 'dash.alertNotArrived', { d: String(a.days) })}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}

/** Barras VERTICALES (hacia arriba), scroll horizontal. Valor arriba, nombre abajo.
 *  `onBar` (2026-08-10): click en la barra/nombre → abre el cliente en SU cola de Payments. */
function VBars({
  items,
  color,
  unit = 'd',
  onBar,
}: {
  items: {
    id: string; claimId?: string; name: string; value: number; sub?: string;
    contractor?: string | null; owner?: string | null;
    /** Bandera "falta un documento": pinta la barra distinto y le pone el 📄 encima. */
    flag?: { color: string; icon: string; title: string } | null;
  }[];
  color: (value: number) => string;
  unit?: string;
  onBar?: (claimId: string) => void;
}) {
  if (items.length === 0) return <div className="muted" style={{ fontSize: 'var(--fs-sm)' }}>—</div>;
  const sorted = [...items].sort((a, b) => b.value - a.value); // mayor a menor
  const max = Math.max(...sorted.map((i) => i.value), 1);
  return (
    <div style={{ display: 'flex', gap: 8, alignItems: 'flex-end', overflowX: 'auto', paddingBottom: 6, minHeight: 170 }}>
      {sorted.map((it) => (
        <div key={it.id}
          onClick={onBar ? () => onBar(it.claimId ?? it.id) : undefined}
          style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', flex: '0 0 auto', cursor: onBar ? 'pointer' : undefined }}>
          <div style={{ fontSize: 'var(--fs-2xs)', fontWeight: 'var(--fw-bold)' }}>
            {it.flag && <span style={{ marginRight: 2 }}>{it.flag.icon}</span>}{it.value}{unit}
          </div>
          {/* Con bandera manda el color de la bandera: el rojo del reloj diría "nadie hizo nada",
              y acá el reloj corre porque falta un papel que tiene que mandar otro. */}
          <div
            title={`${it.name}: ${it.value}${unit}${it.flag ? `\n${it.flag.title}` : ''}${it.contractor ? `\nContractor: ${it.contractor}` : ''}${it.owner ? `\nOwner: ${it.owner}` : ''}${it.sub ? `\n${it.sub}` : ''}`}
            style={{ width: 22, height: Math.max(4, (it.value / max) * 120), background: it.flag ? it.flag.color : color(it.value), borderRadius: '4px 4px 0 0', marginTop: 2 }}
          />
          <div style={{ fontSize: 'var(--fs-2xs)', marginTop: 4, width: 54, textAlign: 'center', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }} title={it.name}>
            {it.name}
          </div>
          {it.sub && <div className="muted" style={{ fontSize: 'var(--fs-2xs)', width: 54, textAlign: 'center', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{it.sub}</div>}
          {/* Payment owner SIEMPRE visible bajo la barra (pedido 2026-08-10) — primer nombre; el completo va en el title. */}
          {it.owner && (
            <div style={{ fontSize: 'var(--fs-2xs)', color: 'var(--accent)', marginTop: 1, width: 54, textAlign: 'center', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }} title={it.owner}>
              {it.owner.split(/\s+/)[0]}
            </div>
          )}
        </div>
      ))}
    </div>
  );
}

/** Sparkline de la serie mensual (línea 2px + área al 10%, --chart-1 — sección 1 del manual). */
function MonthlySpark({ data }: { data: number[] }) {
  const w = 320; const h = 64;
  const min = Math.min(...data); const max = Math.max(...data); const span = max - min || 1;
  const pts = data.map((v, i) => [
    data.length > 1 ? (i / (data.length - 1)) * w : w / 2,
    h - 3 - ((v - min) / span) * (h - 6),
  ]);
  const line = pts.map(([x, y], i) => `${i === 0 ? 'M' : 'L'}${x!.toFixed(1)},${y!.toFixed(1)}`).join(' ');
  return (
    <svg width="100%" height={h} viewBox={`0 0 ${w} ${h}`} preserveAspectRatio="none" aria-hidden="true" style={{ display: 'block', marginTop: 8 }}>
      <path d={`${line} L${w},${h} L0,${h} Z`} fill="var(--chart-1)" opacity={0.1} />
      <path d={line} fill="none" stroke="var(--chart-1)" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}
